import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, Lock, Star, ArrowRight, TrendingUp, TrendingDown, Minus, ChevronUp, ChevronDown, Share2, X, Zap, Heart, Users, Award, Clock, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import GlassBoxBadge from '@/components/GlassBoxBadge';

const LEVELS = [
  { level: 1, name: 'Newcomer', icon: '🌱', points: 0 },
  { level: 2, name: 'Supporter', icon: '💚', points: 100 },
  { level: 3, name: 'Contributor', icon: '⭐', points: 500 },
  { level: 4, name: 'Advocate', icon: '🌟', points: 1500 },
  { level: 5, name: 'Champion', icon: '🏆', points: 3000 },
  { level: 6, name: 'Leader', icon: '👑', points: 6000 },
  { level: 7, name: 'Visionary', icon: '🔮', points: 10000 },
  { level: 8, name: 'Trailblazer', icon: '🚀', points: 20000 },
  { level: 9, name: 'Legend', icon: '💎', points: 50000 },
  { level: 10, name: 'Global Hero', icon: '🌍', points: 100000 },
];

function getLevelInfo(score) {
  let current = LEVELS[0];
  for (const lvl of LEVELS) if (score >= lvl.points) current = lvl;
  const nextLvl = current.level < 10 ? LEVELS[current.level] : null;
  const ptsToNext = nextLvl ? Math.max(0, nextLvl.points - score) : 0;
  return { current, nextLvl, ptsToNext };
}

function buildBadges(totalDonated, projectsCount, volunteerHours, postsCount, communitiesJoined) {
  const defs = [
    { id: 'first-donation', name: 'First Donation', icon: '🎯', category: 'Giving', requirement: 'Make your first donation', threshold: 1, current: Math.min(projectsCount, 1) },
    { id: 'monthly-giver', name: 'Monthly Giver', icon: '📅', category: 'Giving', requirement: 'Donate 3 months in a row', threshold: 3, current: 3 },
    { id: 'century-club', name: 'Century Club', icon: '💯', category: 'Giving', requirement: 'Donate $100+ total', threshold: 100, current: Math.min(totalDonated, 100) },
    { id: 'thousand-strong', name: 'Thousand Strong', icon: '💪', category: 'Giving', requirement: 'Donate $1,000+ total', threshold: 1000, current: Math.min(totalDonated, 1000) },
    { id: 'impact-investor', name: 'Impact Investor', icon: '📈', category: 'Giving', requirement: 'Donate $10,000+ total', threshold: 10000, current: Math.min(totalDonated, 10000) },
    { id: 'first-post', name: 'First Post', icon: '✍️', category: 'Engagement', requirement: 'Share your first update', threshold: 1, current: Math.min(postsCount, 1) },
    { id: 'storyteller', name: 'Storyteller', icon: '📖', category: 'Engagement', requirement: 'Create 10 posts', threshold: 10, current: Math.min(postsCount, 10) },
    { id: 'conversation-starter', name: 'Conversation Starter', icon: '💬', category: 'Engagement', requirement: 'Get 10 comments on your posts', threshold: 10, current: 7 },
    { id: 'community-builder', name: 'Community Builder', icon: '🏘️', category: 'Engagement', requirement: 'Join 5 communities', threshold: 5, current: Math.min(communitiesJoined, 5) },
    { id: 'first-hour', name: 'First Hour', icon: '⏰', category: 'Volunteer', requirement: 'Log your first volunteer hour', threshold: 1, current: Math.min(volunteerHours, 1) },
    { id: 'weekend-warrior', name: 'Weekend Warrior', icon: '🛡️', category: 'Volunteer', requirement: 'Log 20 volunteer hours', threshold: 20, current: Math.min(volunteerHours, 20) },
    { id: 'hundred-hours', name: 'Hundred Hours', icon: '💪', category: 'Volunteer', requirement: 'Log 100 volunteer hours', threshold: 100, current: Math.min(volunteerHours, 100) },
    { id: 'multi-cause', name: 'Multi-Cause', icon: '🌈', category: 'Volunteer', requirement: 'Volunteer for 3+ causes', threshold: 3, current: 2 },
    { id: 'verified-donor', name: 'Verified Donor', icon: '✅', category: 'Impact', requirement: 'All donations verified on-chain', threshold: 1, current: 1 },
    { id: 'disaster-responder', name: 'Disaster Responder', icon: '🆘', category: 'Impact', requirement: 'Donate to a disaster response project', threshold: 1, current: 1 },
    { id: 'education-advocate', name: 'Education Advocate', icon: '📚', category: 'Impact', requirement: 'Support 3+ education projects', threshold: 3, current: 2 },
    { id: 'clean-water-hero', name: 'Clean Water Hero', icon: '💧', category: 'Impact', requirement: 'Support clean water initiatives', threshold: 1, current: 1 },
    { id: 'child-champion', name: 'Child Champion', icon: '👶', category: 'Impact', requirement: "Support children's causes", threshold: 1, current: 1 },
    { id: 'networker', name: 'Networker', icon: '🤝', category: 'Social', requirement: 'Connect with 10 people', threshold: 10, current: 6 },
    { id: 'influencer', name: 'Influencer', icon: '⭐', category: 'Social', requirement: 'Get 50 endorsements', threshold: 50, current: 18 },
    { id: 'early-adopter', name: 'Early Adopter', icon: '🏁', category: 'Social', requirement: 'Joined in the first year', threshold: 1, current: 1 },
  ];
  return defs.map(b => ({
    ...b,
    earned: b.current >= b.threshold,
    progress: Math.min(100, Math.round(b.current / b.threshold * 100)),
    earnedDate: b.current >= b.threshold ? '2026-01-15' : null,
  }));
}

async function buildLeaderboard() {
  try {
    const donors = await fetch('/data/donors.json').then(r => r.json());
    const entries = donors.slice(0, 15).map((d, i) => {
      const score = Math.max(500, d.totalDonated || 0) + (i * 200) + 1000;
      const { current } = getLevelInfo(score);
      return {
        rank: i + 1,
        name: d.name || `User ${i}`,
        avatar: d.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${i}`,
        level: current.level,
        levelName: current.name,
        levelIcon: current.icon,
        impactScore: score,
        trend: [3, -1, 2, 0, 1, -2, 4, 1, -1, 0, 2, 3, -1, 1, 0][i % 15],
      };
    });
    entries.sort((a, b) => b.impactScore - a.impactScore);
    entries.forEach((e, i) => { e.rank = i + 1; });
    return entries;
  } catch {
    return [];
  }
}

async function buildGamificationData() {
  const totalDonated = 32334;
  const projectsCount = 11;
  const volunteerHours = 48;
  const postsCount = 5;
  const communitiesJoined = 3;
  const endorsements = 18;

  const score = Math.floor(totalDonated / 10) + (volunteerHours * 25) + (postsCount * 15) + (3 * 5) + (2 * 5) + (communitiesJoined * 10) + (endorsements * 20) + (5 * 50);
  const { current, nextLvl, ptsToNext } = getLevelInfo(score);
  const badges = buildBadges(totalDonated, projectsCount, volunteerHours, postsCount, communitiesJoined);
  const earnedCount = badges.filter(b => b.earned).length;

  let progressPct = 100;
  if (nextLvl) {
    const rangePts = nextLvl.points - current.points;
    const done = score - current.points;
    progressPct = rangePts > 0 ? Math.min(100, Math.round(done / rangePts * 100)) : 100;
  }

  return {
    impactScore: score,
    level: current.level,
    levelName: current.name,
    levelIcon: current.icon,
    pointsToNextLevel: ptsToNext,
    nextLevelName: nextLvl ? nextLvl.name : null,
    nextLevelPoints: nextLvl ? nextLvl.points : null,
    progressPercent: progressPct,
    totalDonated,
    volunteerHours,
    projectsSupported: projectsCount,
    badgesEarned: earnedCount,
    totalBadges: badges.length,
    badges,
    recentActivity: [
      { icon: '💰', points: 100, text: 'Donated $100 to 3,000 Meals for Tampa Bay', time: '2 hours ago' },
      { icon: '🤝', points: 25, text: 'Logged 1 volunteer hour with Tampa Bay Outreach', time: '1 day ago' },
      { icon: '🏅', points: 50, text: "Earned 'Monthly Giver' badge!", time: '3 days ago' },
      { icon: '✍️', points: 15, text: 'Posted an impact update', time: '5 days ago' },
      { icon: '💬', points: 5, text: 'Commented on Clean Water Kigali update', time: '1 week ago' },
      { icon: '⭐', points: 5, text: 'Followed Rwanda Rising', time: '1 week ago' },
      { icon: '💰', points: 500, text: 'Donated $500 to Solar-Powered Clinics', time: '2 weeks ago' },
      { icon: '🏅', points: 50, text: "Earned 'Verified Donor' badge!", time: '3 weeks ago' },
    ],
    leaderboard: await buildLeaderboard(),
  };
}

function ScoreRing({ progress, score, levelIcon, levelName, level }) {
  const radius = 80;
  const stroke = 8;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (progress / 100) * circ;

  return (
    <div className="relative flex items-center justify-center">
      <svg width={200} height={200} className="transform -rotate-90">
        <circle cx={100} cy={100} r={radius} fill="none" stroke="#E2E8F0" strokeWidth={stroke} />
        <motion.circle
          cx={100} cy={100} r={radius} fill="none" stroke="url(#scoreGradient)" strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
        <defs>
          <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#7C3AED" />
            <stop offset="100%" stopColor="#2563EB" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl mb-1">{levelIcon}</span>
        <motion.span
          className="text-2xl font-bold text-[#111827]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          {score.toLocaleString()}
        </motion.span>
        <span className="text-xs text-gray-500">pts</span>
      </div>
    </div>
  );
}

function BadgeCard({ badge, onClick }) {
  return (
    <button
      onClick={() => onClick(badge)}
      className={`relative p-4 rounded-xl border text-left transition-all hover:shadow-md ${
        badge.earned
          ? 'bg-white border-[#7C3AED]/20 hover:border-[#7C3AED]/40'
          : 'bg-gray-50 border-gray-200 opacity-60'
      }`}
      data-testid={`badge-${badge.id}`}
    >
      {badge.earned && (
        <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#10B981] flex items-center justify-center">
          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
        </div>
      )}
      <div className="text-2xl mb-2">{badge.earned ? badge.icon : '🔒'}</div>
      <p className="text-sm font-semibold text-[#111827] truncate">{badge.name}</p>
      <p className="text-[10px] text-gray-400 mt-0.5">{badge.category}</p>
      {!badge.earned && (
        <div className="mt-2">
          <div className="w-full h-1 rounded-full bg-gray-200 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB]" style={{ width: `${badge.progress}%` }} />
          </div>
          <p className="text-[9px] text-gray-400 mt-0.5">{badge.progress}%</p>
        </div>
      )}
    </button>
  );
}

function BadgeModal({ badge, open, onClose }) {
  if (!badge) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-center">
            <span className="text-4xl block mb-2">{badge.earned ? badge.icon : '🔒'}</span>
            {badge.name}
          </DialogTitle>
        </DialogHeader>
        <div className="text-center space-y-3">
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${badge.earned ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-gray-100 text-gray-500'}`}>
            {badge.earned ? 'Earned' : 'Locked'}
          </span>
          <p className="text-sm text-gray-600">{badge.requirement}</p>
          {!badge.earned && (
            <div className="px-4">
              <div className="w-full h-2 rounded-full bg-gray-200 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB]" style={{ width: `${badge.progress}%` }} />
              </div>
              <p className="text-xs text-gray-400 mt-1">{badge.progress}% complete</p>
            </div>
          )}
          {badge.earned && badge.earnedDate && (
            <p className="text-xs text-gray-400">Earned on {badge.earnedDate}</p>
          )}
          {badge.earned && (
            <Button onClick={() => { toast.success('Badge shared!'); onClose(false); }} variant="outline" className="rounded-full gap-2 text-sm">
              <Share2 className="w-3.5 h-3.5" /> Share this badge
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function trendClass(trend) {
  if (trend > 0) return 'text-[#10B981]';
  if (trend < 0) return 'text-red-400';
  return 'text-gray-400';
}

function trendLabel(trend) {
  if (trend > 0) return `+${trend}`;
  if (trend < 0) return String(trend);
  return '—';
}

function LeaderboardRow({ entry, isCurrentUser }) {
  return (
    <div className={`flex items-center gap-3 py-3 px-4 rounded-lg ${isCurrentUser ? 'bg-[#7C3AED]/5 border border-[#7C3AED]/20' : ''}`} data-testid={`leaderboard-${entry.rank}`}>
      <span className={`text-sm font-bold w-6 text-center ${entry.rank <= 3 ? 'text-[#7C3AED]' : 'text-gray-400'}`}>
        {entry.rank <= 3 ? ['🥇', '🥈', '🥉'][entry.rank - 1] : `#${entry.rank}`}
      </span>
      <img src={entry.avatar} alt="" className="w-8 h-8 rounded-full" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#111827] truncate">{entry.name} {isCurrentUser && <span className="text-xs text-[#7C3AED]">(You)</span>}</p>
        <p className="text-[10px] text-gray-400">{entry.levelIcon} {entry.levelName}</p>
      </div>
      <div className="text-right">
        <p className="text-sm font-bold text-[#111827]">{entry.impactScore.toLocaleString()}</p>
        <p className={`text-[10px] font-medium ${trendClass(entry.trend)}`}>
          {trendLabel(entry.trend)}
        </p>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [showPublic, setShowPublic] = useState(false);

  useEffect(() => {
    buildGamificationData()
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [user]);

  if (loading || !data) return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="animate-pulse space-y-6">
        <div className="h-48 bg-gray-100 rounded-2xl" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-gray-100 rounded-xl" />)}</div>
      </div>
    </div>
  );

  const earnedBadges = data.badges.filter(b => b.earned);
  const lockedBadges = data.badges.filter(b => !b.earned);

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-4 py-6 space-y-8" data-testid="dashboard-page">
      {/* Hero — Impact Score */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-6 sm:p-8"
        data-testid="score-hero"
      >
        <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-10">
          <ScoreRing progress={data.progressPercent} score={data.impactScore} levelIcon={data.levelIcon} levelName={data.levelName} level={data.level} />
          <div className="flex-1 text-center sm:text-left">
            <p className="text-sm text-gray-500 mb-1">Welcome back, {user?.name || 'Sarah'}</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#111827] mb-1">
              {data.levelIcon} {data.levelName} — Level {data.level}
            </h1>
            {data.nextLevelName && (
              <p className="text-sm text-gray-500">
                Next: <span className="font-medium text-[#7C3AED]">{data.nextLevelName}</span> ({data.nextLevelPoints?.toLocaleString()} pts) — <span className="font-semibold text-[#111827]">{data.pointsToNextLevel.toLocaleString()}</span> pts to go
              </p>
            )}
            <div className="mt-3 w-full max-w-xs mx-auto sm:mx-0">
              <div className="w-full h-2.5 rounded-full bg-gray-100 overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB]"
                  initial={{ width: 0 }}
                  animate={{ width: `${data.progressPercent}%` }}
                  transition={{ duration: 1, delay: 0.3 }}
                />
              </div>
            </div>
            <div className="mt-1 flex items-center justify-center sm:justify-start gap-2">
              <GlassBoxBadge label="On-Chain Score" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="stats-row">
        {[
          { icon: Heart, label: 'Total Donated', value: `$${data.totalDonated.toLocaleString()}`, color: 'bg-[#7C3AED]' },
          { icon: Clock, label: 'Volunteer Hours', value: data.volunteerHours, color: 'bg-[#2563EB]' },
          { icon: Target, label: 'Projects Supported', value: data.projectsSupported, color: 'bg-[#10B981]' },
          { icon: Award, label: 'Badges Earned', value: `${data.badgesEarned} / ${data.totalBadges}`, color: 'bg-[#F59E0B]' },
        ].map(s => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${s.color}`}>
              <s.icon className="w-4 h-4 text-white" />
            </div>
            <p className="text-xl font-bold text-[#111827]">{s.value}</p>
            <p className="text-xs text-gray-500">{s.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Badges Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6" data-testid="badges-section">
        <h2 className="text-lg font-bold text-[#111827] mb-4">Badges</h2>
        <Tabs defaultValue="earned">
          <TabsList className="mb-4 bg-gray-100 rounded-lg p-1">
            <TabsTrigger value="earned" className="rounded-md text-xs px-4 data-[state=active]:bg-white">Earned ({earnedBadges.length})</TabsTrigger>
            <TabsTrigger value="locked" className="rounded-md text-xs px-4 data-[state=active]:bg-white">Locked ({lockedBadges.length})</TabsTrigger>
            <TabsTrigger value="all" className="rounded-md text-xs px-4 data-[state=active]:bg-white">All</TabsTrigger>
          </TabsList>
          <TabsContent value="earned">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {earnedBadges.map(b => <BadgeCard key={b.id} badge={b} onClick={setSelectedBadge} />)}
            </div>
          </TabsContent>
          <TabsContent value="locked">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {lockedBadges.map(b => <BadgeCard key={b.id} badge={b} onClick={setSelectedBadge} />)}
            </div>
          </TabsContent>
          <TabsContent value="all">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {data.badges.map(b => <BadgeCard key={b.id} badge={b} onClick={setSelectedBadge} />)}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Activity Feed + Leaderboard side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity Feed */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6" data-testid="activity-feed">
          <h2 className="text-lg font-bold text-[#111827] mb-4">Recent Activity</h2>
          <div className="space-y-3">
            {data.recentActivity.map((a, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 py-2"
              >
                <span className="text-lg shrink-0">{a.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#111827]">{a.text}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{a.time}</p>
                </div>
                <span className="text-sm font-bold text-[#10B981] shrink-0">+{a.points}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Leaderboard */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6" data-testid="leaderboard">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-[#111827]">Impact Leaderboard</h2>
          </div>
          <p className="text-[10px] text-gray-400 mb-3">Participation optional. Ranking visible only to you unless you opt in.</p>
          <div className="flex items-center gap-2 mb-4">
            <button onClick={() => setShowPublic(!showPublic)} className={`w-9 h-5 rounded-full transition-colors relative ${showPublic ? 'bg-[#7C3AED]' : 'bg-gray-300'}`}>
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${showPublic ? 'left-[18px]' : 'left-0.5'}`} />
            </button>
            <span className="text-xs text-gray-500">Show my ranking publicly</span>
          </div>
          <Tabs defaultValue="quarter">
            <TabsList className="mb-3 bg-gray-100 rounded-lg p-1">
              <TabsTrigger value="all-time" className="rounded-md text-[10px] px-3 data-[state=active]:bg-white">All Time</TabsTrigger>
              <TabsTrigger value="quarter" className="rounded-md text-[10px] px-3 data-[state=active]:bg-white">This Quarter</TabsTrigger>
              <TabsTrigger value="month" className="rounded-md text-[10px] px-3 data-[state=active]:bg-white">This Month</TabsTrigger>
            </TabsList>
            <TabsContent value="quarter">
              <div className="space-y-1 max-h-[400px] overflow-y-auto">
                {data.leaderboard.slice(0, 10).map((e, i) => (
                  <LeaderboardRow key={i} entry={e} isCurrentUser={e.name === 'Sarah Chen'} />
                ))}
              </div>
            </TabsContent>
            <TabsContent value="all-time">
              <div className="space-y-1 max-h-[400px] overflow-y-auto">
                {data.leaderboard.map((e, i) => (
                  <LeaderboardRow key={i} entry={e} isCurrentUser={e.name === 'Sarah Chen'} />
                ))}
              </div>
            </TabsContent>
            <TabsContent value="month">
              <div className="space-y-1 max-h-[400px] overflow-y-auto">
                {data.leaderboard.slice(0, 5).map((e, i) => (
                  <LeaderboardRow key={i} entry={e} isCurrentUser={e.name === 'Sarah Chen'} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Suggested Actions */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6" data-testid="suggested-actions">
        <h2 className="text-lg font-bold text-[#111827] mb-4">Ways to Earn More Impact</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { title: 'Donate to a Trending Project', desc: 'Support a verified cause and earn points', pts: '+100 pts potential', link: '/explore', icon: Heart },
            { title: 'Log Volunteer Hours', desc: 'Every hour logged earns 25 impact points', pts: '+25 pts/hour', link: '/volunteer', icon: Clock },
            { title: 'Join a Community', desc: 'Connect with like-minded changemakers', pts: '+10 pts', link: '/communities', icon: Users },
          ].map(a => (
            <Link key={a.title} to={a.link} className="group p-4 rounded-xl border border-gray-200 hover:border-[#7C3AED]/30 hover:shadow-md transition-all">
              <div className="flex items-center gap-2 mb-2">
                <a.icon className="w-5 h-5 text-[#7C3AED]" />
                <span className="text-xs font-semibold text-[#10B981] bg-[#10B981]/10 px-2 py-0.5 rounded-full">{a.pts}</span>
              </div>
              <h3 className="font-semibold text-sm text-[#111827] mb-1">{a.title}</h3>
              <p className="text-xs text-gray-500">{a.desc}</p>
              <span className="text-xs text-[#7C3AED] font-medium mt-2 inline-flex items-center gap-1 group-hover:underline">Go <ArrowRight className="w-3 h-3" /></span>
            </Link>
          ))}
        </div>
      </div>

      <BadgeModal badge={selectedBadge} open={!!selectedBadge} onClose={() => setSelectedBadge(null)} />
    </div>
  );
}
